import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type DragEvent } from 'react'
import ReactFlow, {
  Background,
  BackgroundVariant,
  ReactFlowProvider,
  useReactFlow,
  useNodesInitialized,
  useNodesState,
  useEdgesState,
  addEdge,
  ConnectionMode,
  MarkerType,
  Position,
  useStore,
  type Edge,
  type Node,
  type NodeTypes,
  type OnConnect,
  type DefaultEdgeOptions,
} from 'reactflow'
import 'reactflow/dist/style.css'

import { PlaceholderNode } from '../PlaceholderNode'
import { AssetNode } from '../AssetNode'
import { PipelineFlowStateContext } from '../pipelineFlowStateContext'
import { MaterialIcon } from '@/ui/material-icon'
import canvasStyles from './AdpPipelineFlowCanvas.module.scss'
import {
  assetKeyFromPayload,
  cardTitleFromPayload,
  type DragPayload,
} from '@/features/adp/utils/pipelineAssetUtils'
import type { PipelineGraphBridge } from '../pipelineGraphBridge.types'
import {
  pruneAdpPipelineSessionByNodeIds,
  readAdpPipelineSession,
  replaceAdpPipelineSession,
  type AdpPipelineSessionV1,
} from '@/features/adp/utils/adpPipelineSessionStorage'
import { toast } from 'sonner'

const nodeTypes: NodeTypes = {
  placeholder: PlaceholderNode,
  asset: AssetNode,
}

const edgeStroke = '#99a1af'
const MULTIPLE_DESTINATIONS_NOT_ALLOWED_TOAST = 'Multiple destinations is not allowed'
const INITIAL_AUTO_FIT_MAX_ZOOM = 1.5

const ZOOM_PRESETS = [50, 75, 100, 125, 150]

const arrowEnd = {
  type: MarkerType.ArrowClosed,
  color: edgeStroke,
  width: 18,
  height: 18,
}

let idCounter = 0
const nextId = () => `dropped-${Date.now()}-${idCounter++}`

function getNodeGroupId(node: Node): string | undefined {
  const data = node.data as { groupId?: string } | undefined
  return data?.groupId
}

function isSourceGroup(groupId: string | undefined): boolean {
  return groupId === 'sources'
}

function isDestinationGroup(groupId: string | undefined): boolean {
  return groupId === 'destinations'
}

function isIntegrationGroup(groupId: string | undefined): boolean {
  return groupId === 'integrations'
}

function hasIncomingEdge(nodeId: string, edges: Edge[]): boolean {
  return edges.some(edge => edge.target === nodeId)
}

function hasOutgoingEdge(nodeId: string, edges: Edge[]): boolean {
  return edges.some(edge => edge.source === nodeId)
}

function edgeExists(source: string, target: string, edges: Edge[]): boolean {
  return edges.some(edge => edge.source === source && edge.target === target)
}

function hasDestinationNode(nodes: Node[], excludedNodeId?: string): boolean {
  return nodes.some(node => {
    if (excludedNodeId && node.id === excludedNodeId) return false
    return isDestinationGroup(getNodeGroupId(node))
  })
}

function nodeDistance(a: Node, b: Node): number {
  return Math.hypot(a.position.x - b.position.x, a.position.y - b.position.y)
}

function createAutoEdge(source: string, target: string): Edge {
  return {
    id: `${source}->${target}`,
    source,
    sourceHandle: 'source',
    target,
    targetHandle: 'target',
    type: 'default',
    style: { stroke: edgeStroke, strokeWidth: 2 },
    markerEnd: arrowEnd,
  }
}

function getUpstreamCandidates(nodes: Node[], edges: Edge[], newNode: Node): Node[] {
  const candidates = nodes.filter(node => {
    const groupId = getNodeGroupId(node)
    if (node.id === newNode.id || isDestinationGroup(groupId)) return false
    if (hasOutgoingEdge(node.id, edges)) return false
    return node.position.x <= newNode.position.x + 32
  })

  return candidates.sort((a, b) => {
    const aDeltaX = Math.max(0, newNode.position.x - a.position.x)
    const bDeltaX = Math.max(0, newNode.position.x - b.position.x)

    if (aDeltaX !== bDeltaX) return aDeltaX - bDeltaX
    return nodeDistance(a, newNode) - nodeDistance(b, newNode)
  })
}

function getDownstreamTarget(nodes: Node[], edges: Edge[], newNode: Node): Node | null {
  const candidates = nodes.filter(node => {
    const groupId = getNodeGroupId(node)
    if (node.id === newNode.id || isSourceGroup(groupId)) return false
    if (!isIntegrationGroup(groupId) && hasIncomingEdge(node.id, edges)) return false
    return true
  })

  if (candidates.length === 0) return null

  const preferred = candidates.filter(node => node.position.x >= newNode.position.x - 24)
  const pool = preferred.length > 0 ? preferred : candidates

  pool.sort((a, b) => {
    const aPriority = isIntegrationGroup(getNodeGroupId(a)) ? 0 : 1
    const bPriority = isIntegrationGroup(getNodeGroupId(b)) ? 0 : 1

    if (aPriority !== bPriority) return aPriority - bPriority
    return nodeDistance(a, newNode) - nodeDistance(b, newNode)
  })

  return pool[0] ?? null
}

function getAutoEdgesForNode(nodes: Node[], edges: Edge[], newNode: Node): Edge[] {
  const groupId = getNodeGroupId(newNode)

  if (isSourceGroup(groupId)) {
    const target = getDownstreamTarget(nodes, edges, newNode)
    if (!target || edgeExists(newNode.id, target.id, edges)) return []
    return [createAutoEdge(newNode.id, target.id)]
  }

  const upstream = getUpstreamCandidates(nodes, edges, newNode)
  if (upstream.length === 0) return []

  const sources = isIntegrationGroup(groupId) ? upstream : upstream.slice(0, 1)
  return sources
    .filter(node => !edgeExists(node.id, newNode.id, edges))
    .map(node => createAutoEdge(node.id, newNode.id))
}

export type { DragPayload } from '@/features/adp/utils/pipelineAssetUtils'

export const DRAG_MIME = 'application/akashic-adp-asset'

function FitViewWhenPipelineStarts({
  pipelineStarted,
  setZoomPct,
}: {
  pipelineStarted: boolean
  setZoomPct: (pct: number) => void
}) {
  const { fitView, getZoom } = useReactFlow()
  const nodesInitialized = useNodesInitialized()
  const fittedRef = useRef(false)

  useEffect(() => {
    if (!pipelineStarted) {
      fittedRef.current = false
      return
    }
    if (!nodesInitialized || fittedRef.current) return
    fittedRef.current = true
    const id = requestAnimationFrame(() => {
      fitView({ padding: 0.28, maxZoom: INITIAL_AUTO_FIT_MAX_ZOOM })
      setZoomPct(Math.round(getZoom() * 100))
    })
    return () => cancelAnimationFrame(id)
  }, [pipelineStarted, nodesInitialized, fitView, getZoom, setZoomPct])

  return null
}

function ZoomTracker({ setZoomPct }: { setZoomPct: (pct: number) => void }) {
  const zoom = useStore((state) => state.transform[2])
  useEffect(() => {
    setZoomPct(Math.round(zoom * 100))
  }, [zoom, setZoomPct])
  return null
}

type FlowCanvasInnerProps = {
  onGraphChange?: (snap: { nodes: Node[]; edges: Edge[] }) => void
  onBridgeReady?: (bridge: PipelineGraphBridge) => void
  canEditPipeline?: boolean
  hydrationSnapshot?: FlowHistorySnapshot | null
  hydrationKey?: string | number | null
  onRequirePipelineSetup?: (pendingDrop?: {
    payload: DragPayload
    position: { x: number; y: number }
  }) => void
}

type FlowHistorySnapshot = {
  nodes: Node[]
  edges: Edge[]
  session: AdpPipelineSessionV1
}

const MAX_HISTORY_STEPS = 100

function FlowCanvasInner({
  onGraphChange,
  onBridgeReady,
  canEditPipeline = true,
  hydrationSnapshot,
  hydrationKey,
  onRequirePipelineSetup,
}: FlowCanvasInnerProps) {
  const [zoomPct, setZoomPct] = useState(100)
  const [canvasLocked, setCanvasLocked] = useState(false)
  const [, setHistoryVersion] = useState(0)

  // Use React Flow v11's simpler state management
  const [nodes, setNodes, onNodesChange] = useNodesState([])
  const [edges, setEdges, onEdgesChange] = useEdgesState([])

  const {
    // project,
    screenToFlowPosition,
    zoomIn,
    zoomOut,
    getZoom,
    fitView,
    getViewport,
    setViewport,
  } = useReactFlow()

  const pipelineStarted = nodes.length > 0
  const undoStackRef = useRef<FlowHistorySnapshot[]>([])
  const redoStackRef = useRef<FlowHistorySnapshot[]>([])

  const cloneSnapshot = useCallback(<T,>(value: T): T => {
    return JSON.parse(JSON.stringify(value)) as T
  }, [])

  const captureCurrentSnapshot = useCallback((): FlowHistorySnapshot => {
    return {
      nodes: cloneSnapshot(nodes),
      edges: cloneSnapshot(edges),
      session: cloneSnapshot(readAdpPipelineSession()),
    }
  }, [cloneSnapshot, edges, nodes])

  const pushUndoSnapshot = useCallback(() => {
    undoStackRef.current.push(captureCurrentSnapshot())
    if (undoStackRef.current.length > MAX_HISTORY_STEPS) {
      undoStackRef.current.splice(0, undoStackRef.current.length - MAX_HISTORY_STEPS)
    }
    redoStackRef.current = []
    setHistoryVersion(v => v + 1)
  }, [captureCurrentSnapshot])

  const restoreSnapshot = useCallback((snapshot: FlowHistorySnapshot) => {
    setNodes(cloneSnapshot(snapshot.nodes))
    setEdges(cloneSnapshot(snapshot.edges))
    replaceAdpPipelineSession(cloneSnapshot(snapshot.session))
  }, [cloneSnapshot, setEdges, setNodes])

  const handleUndo = useCallback(() => {
    const prev = undoStackRef.current.pop()
    if (!prev) return
    redoStackRef.current.push(captureCurrentSnapshot())
    restoreSnapshot(prev)
    setHistoryVersion(v => v + 1)
  }, [captureCurrentSnapshot, restoreSnapshot])

  const handleRedo = useCallback(() => {
    const next = redoStackRef.current.pop()
    if (!next) return
    undoStackRef.current.push(captureCurrentSnapshot())
    restoreSnapshot(next)
    setHistoryVersion(v => v + 1)
  }, [captureCurrentSnapshot, restoreSnapshot])

  const onConnect = useCallback<OnConnect>(
    (connection) => {
      console.log('🔥 V11 CONNECTION:', connection)
      pushUndoSnapshot()

      setEdges((eds) => {
        const newEdges = addEdge({
          ...connection,
          type: 'default',
          style: {
            stroke: edgeStroke,
            strokeWidth: 2
          },
          markerEnd: arrowEnd,
        }, eds)

        console.log('✅ V11 EDGES UPDATED:', newEdges.length, 'from', eds.length)
        return newEdges
      })
    },
    [pushUndoSnapshot, setEdges]
  )

  const onDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }, [])

  const addAssetNode = useCallback(
    ({ payload, position }: { payload: DragPayload; position?: { x: number; y: number } }) => {
      if (payload.groupId === 'destinations' && hasDestinationNode(nodes)) {
        toast.error(MULTIPLE_DESTINATIONS_NOT_ALLOWED_TOAST)
        return null
      }

      const newNode: Node = {
        id: nextId(),
        type: 'asset',
        position: position ?? { x: 240, y: 180 },
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
        data: {
          label: payload.label,
          icon: payload.iconImage ? undefined : (payload.icon ?? 'dataset'),
          iconImage: payload.iconImage,
          groupId: payload.groupId,
          connectorId: payload.connectorId,
          titleLine: cardTitleFromPayload(payload),
          assetKey: assetKeyFromPayload(payload),
        },
      }

      pushUndoSnapshot()
      const nextNodes = nodes.concat(newNode)
      setNodes(nextNodes)

      if (nextNodes.length <= 2) {
        setEdges(eds => {
          const autoEdges = getAutoEdgesForNode(nextNodes, eds, newNode)
          if (autoEdges.length === 0) return eds
          return eds.concat(autoEdges.filter(edge => !edgeExists(edge.source, edge.target, eds)))
        })
      }

      return newNode.id
    },
    [nodes, pushUndoSnapshot, setEdges, setNodes]
  )

  const addAssetFromDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      const raw = e.dataTransfer.getData(DRAG_MIME)
      if (!raw) return
      let payload: DragPayload
      try {
        payload = JSON.parse(raw) as DragPayload
      } catch {
        return
      }

      const position = screenToFlowPosition({
        x: e.clientX,
        y: e.clientY,
      })

      if (!canEditPipeline) {
        onRequirePipelineSetup?.({ payload, position })
        return
      }

      addAssetNode({ payload, position })
    },
    [addAssetNode, canEditPipeline, onRequirePipelineSetup, screenToFlowPosition]
  )

  const onDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      addAssetFromDrop(e)
    },
    [addAssetFromDrop]
  )

  const onMoveEnd = useCallback((_: MouseEvent | TouchEvent, viewport: { x: number; y: number; zoom: number }) => {
    setZoomPct(Math.round(viewport.zoom * 100))
  }, [])

  const handleToggleLock = useCallback(() => {
    setCanvasLocked(prev => !prev)
  }, [])

  const handleZoomIn = () => {
    if (canvasLocked) return
    const step = 1.2
    const v = getViewport()
    const nextZoom = Math.min(v.zoom * step, 2)
    setViewport({ x: v.x, y: v.y, zoom: nextZoom })
    setZoomPct(Math.round(nextZoom * 100))
  }

  const handleZoomOut = () => {
    if (canvasLocked) return
    const step = 1.2
    const v = getViewport()
    const nextZoom = Math.max(v.zoom / step, 0.25)
    setViewport({ x: v.x, y: v.y, zoom: nextZoom })
    setZoomPct(Math.round(nextZoom * 100))
  }

  const handleZoomSelect = (e: ChangeEvent<HTMLSelectElement>) => {
    if (canvasLocked) return
    const z = Number(e.target.value) / 100
    const v = getViewport()
    setViewport({ x: v.x, y: v.y, zoom: z })
    setZoomPct(Number(e.target.value))
  }

  const handleFit = () => {
    if (canvasLocked) return
    fitView({ padding: 0.2 })
    setZoomPct(Math.round(getZoom() * 100))
  }

  const defaultEdgeOptions = useMemo<DefaultEdgeOptions>(
    () => ({
      type: 'default',
      style: {
        stroke: edgeStroke,
        strokeWidth: 2,
      },
      markerEnd: arrowEnd,
    }),
    []
  )

  const removeNodeAndConnectedEdges = useCallback(
    (nodeId: string) => {
      pushUndoSnapshot()
      setNodes(nds => nds.filter(n => n.id !== nodeId))
      setEdges(eds => eds.filter(e => e.source !== nodeId && e.target !== nodeId))
      const remainingNodeIds = nodes.filter(n => n.id !== nodeId).map(n => n.id)
      pruneAdpPipelineSessionByNodeIds(remainingNodeIds)
    },
    [nodes, pushUndoSnapshot, setNodes, setEdges]
  )

  useEffect(() => {
    onGraphChange?.({ nodes, edges })
  }, [nodes, edges, onGraphChange])

  useEffect(() => {
    if (!hydrationSnapshot) return
    setNodes(cloneSnapshot(hydrationSnapshot.nodes))
    setEdges(cloneSnapshot(hydrationSnapshot.edges))
    replaceAdpPipelineSession(cloneSnapshot(hydrationSnapshot.session))
    undoStackRef.current = []
    redoStackRef.current = []
    setHistoryVersion(v => v + 1)
  }, [cloneSnapshot, hydrationKey, hydrationSnapshot, setEdges, setNodes])

  const addIntegrationOutputNode = useCallback(
    (args: { integrationNodeId: string; connectorId: 'join' | 'union' }) => {
      const intNode = nodes.find(n => n.id === args.integrationNodeId)
      if (!intNode) return null
      pushUndoSnapshot()
      const newId = nextId()
      setNodes(nds => {
        const label = args.connectorId === 'join' ? 'Joined' : 'Union'
        const newNode: Node = {
          id: newId,
          type: 'asset',
          position: { x: intNode.position.x + 300, y: intNode.position.y },
          sourcePosition: Position.Right,
          targetPosition: Position.Left,
          data: {
            label,
            icon: args.connectorId === 'join' ? 'join_full' : 'merge',
            groupId: 'integrations',
            connectorId: args.connectorId,
            titleLine: args.connectorId === 'join' ? 'Joined result' : 'Union result',
            assetKey: `${args.connectorId}-result`,
            integrationResult: true,
          },
        }
        return nds.concat(newNode)
      })
      setEdges(eds => {
        if (eds.some(e => e.source === args.integrationNodeId && e.target === newId)) return eds
        return eds.concat(createAutoEdge(args.integrationNodeId, newId))
      })
      return newId
    },
    [nodes, pushUndoSnapshot, setNodes, setEdges]
  )

  const clearAllNodes = useCallback(() => {
    pushUndoSnapshot()
    setNodes([])
    setEdges([])
    pruneAdpPipelineSessionByNodeIds([])
  }, [pushUndoSnapshot, setNodes, setEdges])

  useEffect(() => {
    const bridge: PipelineGraphBridge = {
      getSnapshot: () => ({ nodes, edges }),
      addAssetNode,
      addIntegrationOutputNode,
      clearAllNodes,
      toggleCanvasLock: handleToggleLock,
    }
    onBridgeReady?.(bridge)
  }, [nodes, edges, onBridgeReady, addAssetNode, addIntegrationOutputNode, clearAllNodes, handleToggleLock])

  const pipelineFlowStateValue = useMemo(
    () => ({
      setNodes,
      setEdges,
      removeNodeAndConnectedEdges,
    }),
    [setNodes, setEdges, removeNodeAndConnectedEdges]
  )

  return (
    <div className={canvasStyles.flowWrap}>
      <PipelineFlowStateContext.Provider value={pipelineFlowStateValue}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onMoveEnd={onMoveEnd}
          nodeTypes={nodeTypes}
          defaultEdgeOptions={defaultEdgeOptions}
          connectionMode={ConnectionMode.Loose}
          connectionRadius={40}
          isValidConnection={(connection) => {
            console.log('🔍 V11 VALIDATING:', connection)
            return connection.source !== connection.target
          }}
          fitView={false}
          minZoom={0.25}
          maxZoom={2}
          className={canvasStyles.reactFlow}
          style={{ width: '100%', height: '100%' }}
          nodesDraggable={pipelineStarted && !canvasLocked}
          nodesConnectable={pipelineStarted && !canvasLocked}
          elementsSelectable={pipelineStarted && !canvasLocked}
          panOnDrag={pipelineStarted && !canvasLocked}
          zoomOnScroll={pipelineStarted && !canvasLocked}
          zoomOnPinch={pipelineStarted && !canvasLocked}
          zoomOnDoubleClick={pipelineStarted && !canvasLocked}
        >
          <FitViewWhenPipelineStarts pipelineStarted={pipelineStarted} setZoomPct={setZoomPct} />
          <ZoomTracker setZoomPct={setZoomPct} />
          <Background variant={BackgroundVariant.Dots} gap={16} size={1} color="#cbd5e1" />

          {/* V11 Debug panel */}


          {!pipelineStarted && (
            <div
              className={canvasStyles.emptyOverlay}
              onDrop={addAssetFromDrop}
              onDragOver={onDragOver}
              role="region"
              aria-label="Pipeline workspace"
            >
              <div className={canvasStyles.emptyOverlayInner}>
                <p className={canvasStyles.emptyTitle}>New Project</p>
                <p className={canvasStyles.emptySub}>Drag and drop assets here to build a pipeline</p>
                <img
                  className={canvasStyles.emptyIllustration}
                  src="/pipeline-empty-state.png"
                  alt=""
                />
              </div>
            </div>
          )}

          <div className={canvasStyles.floatingToolbar}>
            <button type="button" className={canvasStyles.ftIcon} aria-label="Fit view" onClick={handleFit}>
              <MaterialIcon name="refresh" size={20} />
            </button>
            <button
              type="button"
              className={canvasStyles.ftIcon}
              aria-label="Undo"
              onClick={handleUndo}
              disabled={undoStackRef.current.length === 0}
            >
              <MaterialIcon name="undo" size={20} />
            </button>
            <button
              type="button"
              className={canvasStyles.ftIcon}
              aria-label="Redo"
              onClick={handleRedo}
              disabled={redoStackRef.current.length === 0}
            >
              <MaterialIcon name="redo" size={20} />
            </button>
            <button
              type="button"
              className={`${canvasStyles.ftIconRound} ${canvasLocked ? canvasStyles.ftIconActive : ''}`}
              aria-label={canvasLocked ? 'Unlock canvas' : 'Lock canvas'}
              onClick={handleToggleLock}
            >
              <MaterialIcon name={canvasLocked ? 'lock' : 'lock_open'} size={22} />
            </button>
            <button type="button" className={canvasStyles.ftIcon} aria-label="Zoom out" onClick={handleZoomOut}>
              <MaterialIcon name="remove" size={20} />
            </button>
            <select
              className={canvasStyles.zoomSelect}
              aria-label="Zoom level"
              value={zoomPct}
              onChange={handleZoomSelect}
            >
              {!ZOOM_PRESETS.includes(zoomPct) && (
                <option value={zoomPct}>{zoomPct}%</option>
              )}
              <option value={50}>50%</option>
              <option value={75}>75%</option>
              <option value={100}>100%</option>
              <option value={125}>125%</option>
              <option value={150}>150%</option>
            </select>
            <button type="button" className={canvasStyles.ftIcon} aria-label="Zoom in" onClick={handleZoomIn}>
              <MaterialIcon name="add" size={20} />
            </button>
          </div>
        </ReactFlow>
      </PipelineFlowStateContext.Provider>
    </div>
  )
}

export type AdpPipelineFlowCanvasProps = FlowCanvasInnerProps

export function AdpPipelineFlowCanvas(props: AdpPipelineFlowCanvasProps) {
  return (
    <ReactFlowProvider>
      <FlowCanvasInner {...props} />
    </ReactFlowProvider>
  )
}
